import type {
  GpbDefinition, GpbEvidence, GpbGoal, GpbGoalResult, GpbPredicate,
  GpbProgress, GpbQuest, GpbQuestResult, GpbRank, GpbRankResult,
} from '../../types/gpb';

/**
 * ⭐⭐ **THE COMPLETION ENGINE.** Given a build definition, a rank window and
 * what the person has actually done, this answers "how far along is each goal"
 * — and nothing else. It creates no records, awards no XP and reads no store.
 *
 * ⚠️⚠️ **REFERENCE-ONLY, AND THAT IS A CONTRACT.** The integration brief:
 * *"For GPB-derived goals, quests and ranks, use reference-only progress with no
 * new action XP or GPB bonus."* The native action keeps its own reward
 * behaviour; the pathway adds nothing. A function that returns numbers cannot
 * violate that by accident — which is most of why it is pure.
 *
 * ⚠️ **THE WINDOW IS A FLOOR, NOT A RANGE.** Each rank counts evidence from the
 * moment its window opens, with no upper bound: work done later still counts,
 * and work done before the rank opened does not. The package calls this the
 * fresh window and sets it to the next local day after the previous rank
 * completes.
 */

/** `YYYY-MM-DD` compares correctly as a string, which is why dates are stored so. */
const onOrAfter = (date: string, from: string): boolean => date >= from;

const progress = (have: number, need: number): GpbProgress => ({
  have: Math.min(have, need),
  need,
  done: have >= need,
});

/**
 * ⭐⭐ **HOW MANY A PREDICATE ASKS FOR, WITHOUT ANY EVIDENCE.**
 *
 * The denominator only — "seven planned sessions" is 7 here whether or not a
 * single one has been done. `evaluatePredicate` uses it for the `need` side of
 * every `progress()`, and `releaseDiff` uses it to put two versions' counts side
 * by side (D26's second requirement).
 *
 * ⚠️⚠️ **THERE MUST BE EXACTLY ONE OF THESE.** The release review shows the number
 * a goal asks for and the evaluator decides whether it has been met; if those two
 * read the denominator from separate code, a release can announce "now asks for
 * ten" while the engine keeps requiring seven, and every test still passes. This
 * codebase already carries two different `have/need` pairs for two different
 * questions — a third, silently disagreeing with the evaluator, is the one that
 * would be a lie to the user.
 *
 * ⚠️ An unresolvable `tasks` project falls back to 1, matching the evaluator's
 * fail-closed branch: a goal referencing a project that does not exist asks for
 * something impossible rather than for nothing.
 */
export function needOf(pred: GpbPredicate, def: GpbDefinition): number {
  switch (pred.kind) {
    case 'distinct_dates': return pred.count;
    case 'occurrences':    return pred.mode === 'any' ? 1 : pred.ids.length;
    case 'skills':         return pred.ids.length;
    case 'tasks':          return def.projects.find(p => p.id === pred.project)?.tasks.length ?? 1;
    /* ⚠️ `used` is a second step, not a flag — saving a plan is not applying it. */
    case 'record':         return pred.used ? 2 : 1;
  }
}

/**
 * When a predicate carries `after`, its evidence must post-date the *earliest*
 * evidence of the predicate it names.
 *
 * ⚠️⚠️ This is the package's plan-before-application rule, and it is a real
 * requirement rather than a nicety: a saved adjustment must predate the two
 * applications claimed under it, and a continuation arrangement must predate the
 * practice claimed under it. Without it, someone writes the plan afterwards and
 * the record says they planned ahead.
 */
function earliestAtOrAfter(pred: GpbPredicate, ev: GpbEvidence, from: string): string | null {
  const dates = evidenceDates(pred, ev).filter(d => onOrAfter(d, from));
  return dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : null;
}

/**
 * The date a predicate starts counting from.
 *
 * ⚠️⚠️ **A CHAIN RESOLVES ITS TARGET'S OWN CONSTRAINTS FIRST.** Rank 7 asks for
 * two reviews "with an actual subsequent practice occurrence between them" —
 * review, then practice, then review — and a first draft took the earliest
 * evidence MATCHING the target regardless of whether that evidence itself
 * qualified. A practice performed before the first review would then have set
 * the bar, and the first review could have satisfied the second link. The chain
 * has to be resolved from the top.
 *
 * ⚠️ `seen` guards a definition whose `after` references form a cycle: it fails
 * closed rather than recursing for ever.
 */
function startsFrom(
  pred: GpbPredicate, ev: GpbEvidence, windowOpensAt: string,
  siblings: readonly GpbPredicate[], seen: readonly string[] = [],
): string | null {
  /* Only repetition is window-bound. Everything else counts a lifetime unless
     the goal asked for fresh evidence — see `fresh` in the schema. */
  const base = pred.kind === 'distinct_dates' || pred.fresh ? windowOpensAt : '';
  if (!pred.after) return base;
  if (pred.ref && seen.includes(pred.ref)) return null;

  const target = siblings.find(p => p.ref === pred.after);
  if (!target) return null;
  const targetFrom = startsFrom(target, ev, windowOpensAt, siblings,
    pred.ref ? [...seen, pred.ref] : seen);
  if (targetFrom === null) return null;

  const at = earliestAtOrAfter(target, ev, targetFrom);
  if (at === null) return null;
  return at > base ? at : base;
}

function evidenceDates(pred: GpbPredicate, ev: GpbEvidence): string[] {
  switch (pred.kind) {
    case 'distinct_dates':
      return [...(pred.per === 'day' ? ev.habitDates[pred.habit] : ev.habitOpportunities[pred.habit]) ?? []];
    case 'occurrences':
      return (ev.occurrences[pred.program] ?? []).filter(o => pred.ids.includes(o.id)).map(o => o.date);
    case 'record': {
      const rec = ev.records[pred.record];
      return rec ? [rec.savedAt] : [];
    }
    case 'skills':
      return pred.ids.map(id => ev.skills[id]).filter((d): d is string => !!d);
    case 'tasks':
      return Object.values(ev.tasks[pred.project] ?? {});
  }
}

function evaluatePredicate(
  pred: GpbPredicate, ev: GpbEvidence, windowOpensAt: string, def: GpbDefinition, siblings: readonly GpbPredicate[],
): GpbProgress {
  /* An unresolved chain is a definition error or unmet evidence, and either way
     failing closed is the safe direction for a completion engine. */
  const from = startsFrom(pred, ev, windowOpensAt, siblings);
  if (from === null) return progress(0, 1);

  switch (pred.kind) {
    case 'distinct_dates': {
      const raw = (pred.per === 'day' ? ev.habitDates[pred.habit] : ev.habitOpportunities[pred.habit]) ?? [];
      /* ⚠️⚠️ DISTINCT is the whole point — "at most one qualifying session per
         local day". Two entries on one date are one date. */
      const seen = new Set(raw.filter(d => onOrAfter(d, from)));
      return progress(seen.size, needOf(pred, def));
    }

    case 'occurrences': {
      /* ⚠️ `variant` filters on HOW the occurrence was performed. An ordinary
         session must not satisfy a goal that asks for its alternative version. */
      const done = (ev.occurrences[pred.program] ?? [])
        .filter(o => onOrAfter(o.date, from))
        .filter(o => pred.variant === undefined || o.variant === pred.variant);
      const hit = pred.ids.filter(id => done.some(o => o.id === id));
      if (pred.mode === 'any') return progress(hit.length > 0 ? 1 : 0, needOf(pred, def));
      if (pred.ordered) {
        /* Longest prefix of `ids` whose completions are non-decreasing in date. */
        let last = '';
        let run = 0;
        for (const id of pred.ids) {
          const at = done.find(o => o.id === id)?.date;
          if (at === undefined || at < last) break;
          last = at;
          run += 1;
        }
        return progress(run, needOf(pred, def));
      }
      return progress(hit.length, needOf(pred, def));
    }

    case 'skills':
      return progress(pred.ids.filter(id => !!ev.skills[id]).length, needOf(pred, def));

    case 'tasks': {
      const project = def.projects.find(p => p.id === pred.project);
      if (!project) return progress(0, 1);
      const done = ev.tasks[pred.project] ?? {};
      return progress(project.tasks.filter(t => !!done[t.id]).length, needOf(pred, def));
    }

    case 'record': {
      const rec = ev.records[pred.record];
      if (!rec || !onOrAfter(rec.savedAt, from)) return progress(0, needOf(pred, def));
      const complete = pred.fields.every(f => rec.fields.includes(f));
      if (!complete) return progress(0, needOf(pred, def));
      /* ⚠️ `used` is a second step, not a flag: saving a plan is not applying it. */
      if (!pred.used) return progress(1, needOf(pred, def));
      return progress(rec.usedAt && rec.usedAt >= rec.savedAt ? 2 : 1, needOf(pred, def));
    }
  }
}

/**
 * The date a predicate became satisfied, or null while it is not.
 *
 * ⚠️⚠️ **THE DATE THE REQUIREMENT WAS MET, NOT THE LATEST THING THAT COUNTS
 * TOWARD IT.** Seven sessions are satisfied by the SEVENTH, and an eighth a
 * fortnight later must not move the answer — the next rank's window is derived
 * from this, and a later date would silently discard everything between.
 */
function satisfiedOn(
  pred: GpbPredicate, ev: GpbEvidence, from: string, def: GpbDefinition,
): string | null {
  const sorted = evidenceDates(pred, ev).filter(d => onOrAfter(d, from)).sort();
  switch (pred.kind) {
    case 'distinct_dates': {
      const distinct = [...new Set(sorted)];
      return distinct.length >= pred.count ? distinct[pred.count - 1] : null;
    }
    case 'occurrences': {
      if (pred.mode === 'any') return sorted[0] ?? null;
      const ids = (ev.occurrences[pred.program] ?? []).filter(o => onOrAfter(o.date, from));
      const hit = pred.ids.map(id => ids.find(o => o.id === id)?.date);
      return hit.every(Boolean) ? hit.sort()[hit.length - 1]! : null;
    }
    case 'skills':
      return pred.ids.every(id => !!ev.skills[id]) ? sorted[sorted.length - 1] ?? null : null;
    case 'tasks': {
      const project = def.projects.find(p => p.id === pred.project);
      const done = ev.tasks[pred.project] ?? {};
      return project?.tasks.every(t => !!done[t.id])
        ? project.tasks.map(t => done[t.id]).sort().reverse()[0] ?? null : null;
    }
    case 'record': {
      const rec = ev.records[pred.record];
      if (!rec || !onOrAfter(rec.savedAt, from)) return null;
      if (!pred.fields.every(f => rec.fields.includes(f))) return null;
      if (!pred.used) return rec.savedAt;
      return rec.usedAt && rec.usedAt >= rec.savedAt ? rec.usedAt : null;
    }
  }
}

/** The latest of several, or null if any is missing — "all of them are done". */
const allDoneOn = (dates: readonly (string | null)[]): string | null =>
  dates.some(d => d === null) ? null : dates.reduce((a, b) => (a! > b! ? a : b), '') || null;

export function evaluateGoal(
  goal: GpbGoal, ev: GpbEvidence, windowOpensAt: string, def: GpbDefinition,
): GpbGoalResult {
  const parts = goal.all.map(p => evaluatePredicate(p, ev, windowOpensAt, def, goal.all));
  const done = parts.filter(p => p.done).length;
  const completedOn = done === goal.all.length
    ? allDoneOn(goal.all.map(p => satisfiedOn(p, ev, startsFrom(p, ev, windowOpensAt, goal.all) ?? windowOpensAt, def)))
    : null;
  return { id: goal.id, parts, completedOn, ...progress(done, goal.all.length) };
}

export function evaluateQuest(
  quest: GpbQuest, ev: GpbEvidence, windowOpensAt: string, def: GpbDefinition,
): GpbQuestResult {
  const goals = quest.goals.map(g => evaluateGoal(g, ev, windowOpensAt, def));
  return {
    id: quest.id, goals,
    completedOn: allDoneOn(goals.map(g => g.completedOn)),
    ...progress(goals.filter(g => g.done).length, goals.length),
  };
}

/**
 * A rank passes when **all of its quests pass** — nothing else.
 *
 * ⚠️⚠️ The package says this repeatedly and it is worth keeping in one place:
 * *"These are the entire Rank 1 requirements. No additional assessment,
 * performance benchmark, streak or time-allocation condition exists outside the
 * two quests."* No hours, no streak, no loadout condition, ever.
 */
export function evaluateRank(
  rank: GpbRank, ev: GpbEvidence, windowOpensAt: string, def: GpbDefinition,
): GpbRankResult {
  const quests = rank.quests.map(q => evaluateQuest(q, ev, windowOpensAt, def));
  return {
    rank: rank.rank, quests,
    completedOn: allDoneOn(quests.map(q => q.completedOn)),
    ...progress(quests.filter(q => q.done).length, quests.length),
  };
}
